/**
 * ============================================================================
 * DEV-MODE MOCK-AUTH HARNESS  (Phase 4c)
 * ============================================================================
 * Seeds enough mock state — gated entirely behind `DEV_MODE` — that the guided
 * product tour can run end-to-end in `VITE_DEV_MODE=true` mode with NO backend.
 *
 * Two halves:
 *   1. `seedDevData()`     — writes the `dev_*` localStorage keys the existing
 *                            api.ts DEV_MODE branches already read. The app's
 *                            normal fetch flow (App.tsx) then hydrates the
 *                            workspace / preferences stores from them.
 *   2. `installTourDevApi()` — attaches `window.__zupaTourDev`, a small driver
 *                            so Playwright can fire any tour phase
 *                            deterministically (no live Gemini needed).
 *
 * EVERYTHING here is dead code in production: every export early-returns when
 * `DEV_MODE` is false (a build-time-constant `false`, so the bodies are
 * tree-shaken). Nothing in this module changes production behaviour.
 *
 * This module ONLY augments the DEV_MODE bootstrap — it does not touch tour
 * logic, component rendering, or business logic. The phase→steps map reuses the
 * exact step arrays the real triggers use (imported from tourSteps.ts), so the
 * driver walks the same tour a real user sees.
 * ============================================================================
 */
import { DEV_MODE, MOCK_USER } from '@/lib/devMode';
import {
  DEV_KEYS,
  DEV_PSYCH_SELECTION,
  buildDevCollections,
  buildDevFacts,
  buildDevGeneratedPlatform,
  buildDevKeywords,
  buildDevPosts,
  buildDevSessions,
  buildDevSocialAccounts,
  devWrite,
  installDevHarness,
} from '@/lib/devFixtures';
import { getTourTier } from '@/lib/tourTier';
import { useTourStore, type TourPhase } from '@/stores/tourStore';
import { usePreferencesStore } from '@/stores/preferencesStore';
import { useWorkspaceStore } from '@/stores/workspaceStore';
import { usePostsStore } from '@/stores/postsStore';
import { startTourWithMobileMenu } from '@/components/tour/tourMobileMenu';
import type { User } from '@/types';
import type { TourStep } from '@/components/tour/tourTypes';
import {
  onboardingPreAnalyzeSteps,
  onboardingPostAnalyzeSteps,
  onboardingRegionSteps,
  onboardingServicesSteps,
  onboardingVoiceSteps,
  onboardingExamplesSteps,
  onboardingFactsSteps,
  onboardingPersonasSteps,
  getToolIntroPreGenSteps,
  getToolIntroPostGenSteps,
  toolOrientationSteps,
  deepDiveFeedSteps,
  deepDiveWorkspacesPageSteps,
  deepDiveCaseStudiesSteps,
  deepDiveWorkspacesProfileSteps,
  deepDiveWorkspacesServicesSteps,
  deepDiveWorkspacesVoiceSteps,
  deepDiveWorkspacesPersonasSteps,
  deepDiveDashboardSteps,
  deepDiveMyPostsSteps,
  deepDiveCollectionsSteps,
  deepDiveConnectionsSteps,
  deepDiveSubscriptionSteps,
  deepDivePreferencesSteps,
} from '@/components/tour/tourSteps';

// Stable workspace id for every dev_* key.
const WORKSPACE_ID = 'ws-dev-1';
const now = () => new Date().toISOString();

// ── Mock workspace (rich: brand DNA + profile fields, onboarded) ────────────
const MOCK_WORKSPACE = {
  id: WORKSPACE_ID,
  name: 'Northwind Studio',
  logo_url: null,
  company_url: 'https://northwind.studio',
  company_name: 'Northwind Studio',
  industry: 'B2B SaaS — design tooling',
  description:
    'We build collaborative design-system tooling for product teams — a single source of truth for tokens, components, and docs.',
  target_audience:
    'Design-system leads and senior product designers at 50–500 person software companies.',
  brand_voice: 'Direct, builder-to-builder, lightly witty. No corporate fluff.',
  default_tone: 'professional',
  // Visual Brand DNA
  brand_colors: ['#12263A', '#0EA5E9', '#111827'],
  primary_color: '#12263A',
  visual_style: 'Clean, high-contrast, generous whitespace',
  photography_style: 'Candid team-at-work shots, soft daylight',
  visual_mood: 'Focused, modern, optimistic',
  brand_fonts: ['Inter', 'Lato'],
  logo_description: 'A compass rose mark in violet',
  extracted_logo_url: null,
  visual_brand_analyzed: true,
  // Company Voice (full settings live under dev_ws_voice_<id>)
  brand_voice_settings: null,
  workspace_type: 'company' as const,
  company_stage: 'growing' as const,
  // Metadata
  is_default: true,
  is_onboarded: true,
  created_at: now(),
  updated_at: now(),
  deleted_at: null,
};

// ── Mock services (≥2 so services-card / services-edit-button resolve) ──────
const MOCK_WS_SERVICES = [
  {
    id: 'svc-dev-1',
    workspace_id: WORKSPACE_ID,
    name: 'Design System Platform',
    description: 'Hosted token + component source of truth with live docs.',
    display_order: 0,
    is_active: true,
    created_at: now(),
    updated_at: now(),
  },
  {
    id: 'svc-dev-2',
    workspace_id: WORKSPACE_ID,
    name: 'Migration Service',
    description: 'Hands-on help moving a legacy UI library onto the platform.',
    display_order: 1,
    is_active: true,
    created_at: now(),
    updated_at: now(),
  },
];

// ── Mock voice (tone + ≥3 samples + analysis so voice-extracted resolves) ───
const MOCK_WS_VOICE = {
  voice_tone: ['Confident', 'Friendly'],
  writing_samples: [
    "We shipped tokens-as-code this week. Here's what broke and how we fixed it.",
    'Most design systems die in a Figma file nobody opens. Ours lives where engineers already are.',
    "Stop hand-syncing colors. Define them once, consume them everywhere. That's the whole pitch.",
  ],
  founder_story:
    'Started Northwind after watching three teams rebuild the same button for the fourth time.',
  key_phrases_use: ['source of truth', 'tokens-as-code', 'ship it'],
  key_phrases_avoid: ['leverage', 'synergy', 'best-in-class'],
  content_themes: ['design ops', 'developer experience', 'shipping culture'],
  anti_patterns: ['humble-bragging', 'engagement-bait questions'],
  // Returned by api.getCompanyVoice via spread → CompanyVoiceTab shows the
  // Voice Profile card (data-tour="voice-extracted" / "voice-analyze-button").
  voice_analysis: {
    sentence_style: {
      avg_length: 'short',
      uses_fragments: true,
      uses_questions: true,
      paragraph_length: 'short',
    },
    vocabulary_level: 'simple',
    perspective: 'first_person',
    formatting_preferences: {
      uses_emojis: false,
      uses_bullets: false,
      uses_bold: true,
      uses_hashtags: false,
    },
    content_patterns: {
      leads_with_data: true,
      uses_stories: true,
      uses_examples: true,
      asks_audience: true,
    },
    signature_phrases: ['Here is what broke...', 'The whole pitch is...'],
    words_they_use: ['shipped', 'built', 'broke', 'fixed'],
    words_they_avoid: ['leverage', 'synergy'],
    overall_vibe: 'Punchy, candid builder sharing real lessons.',
    analyzed_at: now(),
    samples_hash: 'dev-mock',
  },
};

// ── Mock personas (≥2 so personas-card / personas-edit-button resolve) ──────
const MOCK_WS_PERSONAS = [
  {
    id: 'persona-dev-1',
    workspace_id: WORKSPACE_ID,
    name: 'Priya — Design Systems Lead',
    description: 'Owns the design system at a 200-person SaaS company.',
    age_range: '30-40',
    job_titles: ['Design Systems Lead', 'Staff Product Designer'],
    seniority_level: 'senior',
    industries: ['Software', 'B2B SaaS'],
    location_tendency: 'Hybrid, major tech hubs',
    motivations: ['Consistency at scale', 'Less rework for her team'],
    frustrations: ['Designers and engineers drifting out of sync', 'No single source of truth'],
    values: ['Craft', 'Pragmatism'],
    aspirations: ['Be the team that ships fast without breaking the brand'],
    is_primary: true,
    created_at: now(),
    updated_at: now(),
  },
  {
    id: 'persona-dev-2',
    workspace_id: WORKSPACE_ID,
    name: 'Marcus — Frontend Eng Manager',
    description: 'Leads a frontend platform team; cares about DX.',
    age_range: '35-45',
    job_titles: ['Engineering Manager', 'Frontend Platform Lead'],
    seniority_level: 'manager',
    industries: ['Software'],
    location_tendency: 'Remote-first',
    motivations: ['Fewer UI bugs', 'Faster onboarding for new devs'],
    frustrations: ['Copy-pasted components', 'Tokens living in a doc nobody updates'],
    values: ['Reliability', 'Developer experience'],
    aspirations: ['A component library engineers actually trust'],
    is_primary: false,
    created_at: now(),
    updated_at: now(),
  },
];

// ── Mock preferences (fresh: tour auto-fires) ───────────────────────────────
const MOCK_PREFERENCES = {
  default_tone: 'professional',
  default_platforms: ['linkedin', 'instagram'],
  email_notifications: true,
  marketing_emails: false,
  enter_to_send: false,
  has_completed_tutorial: false,
  tutorial_dismissed_at: null as string | null,
};

/**
 * Write all dev_* localStorage keys. Always overwrites when DEV_MODE so every
 * page load starts from a known, tour-ready state (lets a refresh act as a full
 * reset for QA). No-op in production.
 *
 * The content keys (posts, collections, facts, history, connections) are seeded
 * from `devFixtures` so the app is LIVED-IN on first load: My Posts, the Feed,
 * Collections, the Fact Bank, Connections and the tool's History panel all have
 * something in them. They used to be permanently blank in dev because the api.ts
 * mocks behind them were a hardcoded `return []` — which is how four real bugs
 * survived a whole session of "verification" on surfaces nobody could see.
 *
 * Mutations (save a post, favourite it, delete a collection) write back to these
 * same keys, so a change is real for the rest of the session and a reload resets
 * it. That reset is deliberate: it keeps QA runs deterministic.
 */
export function seedDevData(): void {
  if (!DEV_MODE) return;
  try {
    localStorage.setItem('dev_user', JSON.stringify(MOCK_USER));
    localStorage.setItem('dev_preferences', JSON.stringify(MOCK_PREFERENCES));
    localStorage.setItem('dev_workspaces', JSON.stringify([MOCK_WORKSPACE]));
    localStorage.setItem('dev_active_workspace_id', WORKSPACE_ID);
    localStorage.setItem(`dev_ws_services_${WORKSPACE_ID}`, JSON.stringify(MOCK_WS_SERVICES));
    localStorage.setItem(`dev_ws_voice_${WORKSPACE_ID}`, JSON.stringify(MOCK_WS_VOICE));
    localStorage.setItem(`dev_ws_personas_${WORKSPACE_ID}`, JSON.stringify(MOCK_WS_PERSONAS));
    localStorage.setItem('dev_onboarding_completed', 'true');

    // Content — the surfaces that were permanently empty in dev.
    devWrite(DEV_KEYS.posts, buildDevPosts());
    devWrite(DEV_KEYS.collections, buildDevCollections());
    devWrite(DEV_KEYS.facts, buildDevFacts());
    devWrite(DEV_KEYS.sessions, buildDevSessions());
    devWrite(DEV_KEYS.social, buildDevSocialAccounts());
    devWrite(DEV_KEYS.keywords, buildDevKeywords());
  } catch {
    // localStorage unavailable (private mode etc.) — dev-only, safe to ignore.
  }
}

/** Attach `window.__zupaDev` (fault injection + latency + reseed). No-op in prod. */
export function installDevApi(): void {
  installDevHarness(seedDevData);
}

// ── window.__zupaTourDev — deterministic phase driver for Playwright ─────────

/** Maps each phase to the exact step array its real trigger uses. */
function stepsForPhase(phase: Exclude<TourPhase, null>): TourStep[] {
  const tier = getTourTier(MOCK_USER as unknown as User);
  switch (phase) {
    case 'onboarding':
      return onboardingPreAnalyzeSteps;
    case 'onboarding_post':
      return onboardingPostAnalyzeSteps;
    case 'onboarding_region':
      return onboardingRegionSteps;
    case 'onboarding_services':
      return onboardingServicesSteps;
    case 'onboarding_voice':
      return onboardingVoiceSteps;
    case 'onboarding_examples':
      return onboardingExamplesSteps;
    case 'onboarding_facts':
      return onboardingFactsSteps;
    case 'onboarding_personas':
      return onboardingPersonasSteps;
    case 'tool_orientation':
      return toolOrientationSteps;
    case 'tool_intro':
      return getToolIntroPreGenSteps(tier);
    case 'tool_intro_post':
      return getToolIntroPostGenSteps();
    case 'deep_dive_feed':
      return deepDiveFeedSteps;
    case 'deep_dive_workspaces_page':
      return deepDiveWorkspacesPageSteps;
    case 'deep_dive_case_studies':
      return deepDiveCaseStudiesSteps;
    case 'deep_dive_workspaces_profile':
      return deepDiveWorkspacesProfileSteps;
    case 'deep_dive_workspaces_services':
      return deepDiveWorkspacesServicesSteps;
    case 'deep_dive_workspaces_voice':
      return deepDiveWorkspacesVoiceSteps;
    case 'deep_dive_workspaces_personas':
      return deepDiveWorkspacesPersonasSteps;
    case 'deep_dive_dashboard':
      return deepDiveDashboardSteps;
    case 'deep_dive_myposts':
      return deepDiveMyPostsSteps;
    case 'deep_dive_collections':
      return deepDiveCollectionsSteps;
    case 'deep_dive_connections':
      return deepDiveConnectionsSteps;
    case 'deep_dive_subscription':
      return deepDiveSubscriptionSteps;
    case 'deep_dive_preferences':
      return deepDivePreferencesSteps;
  }
}

// The tour harness and the real dev generate path (api.ts generatePostsStream)
// now read the SAME fixtures, so the harness can never simulate something the
// mock does not actually produce — the exact drift that let a post-gen tour
// trigger which could never fire survive a whole session of "verification".
const MOCK_PSYCH_SELECTION = DEV_PSYCH_SELECTION;

function buildPlatformData(platform: string) {
  return buildDevGeneratedPlatform(platform);
}

interface TourDevApi {
  startPhase: (phase: Exclude<TourPhase, null>) => void;
  populateGenerated: () => void;
  simulateGeneration: () => Promise<void>;
  endPhase: () => void;
  clearGenerated: () => void;
  resetTutorial: () => void;
  closeModals: () => void;
  getState: () => Record<string, unknown>;
}

/**
 * Attach `window.__zupaTourDev`. No-op in production.
 *
 * `startPhase` routes deep-dive phases through `pendingPhase` so each
 * destination page's OWN trigger (the bespoke Workspaces effect, or
 * `useDeepDiveTrigger` elsewhere) opens any modal / clicks the right tab /
 * polls for content before starting — exactly as the real chain does. Standalone
 * phases (onboarding*, tool_intro*) are started directly via setSteps + startTour.
 */
export function installTourDevApi(): void {
  if (!DEV_MODE) return;
  if (typeof window === 'undefined') return;

  const api: TourDevApi = {
    startPhase: (phase) => {
      const tour = useTourStore.getState();
      // Clear any in-flight tour / modal so the new phase starts clean.
      tour.closeWelcomeModal();
      tour.closeCheckpointModal();
      tour.closeCloseConfirmModal();
      tour.closeEndScreen();
      void tour.endTour(false);
      // Let endTour's state flush, then start. Deep-dive phases defer to the
      // page's own trigger (modal/tab/poll); others start directly.
      setTimeout(() => {
        const t = useTourStore.getState();
        if (phase.startsWith('deep_dive')) {
          // Defer to the destination page's own trigger (opens modal / clicks
          // tab / polls for content), exactly as the real chain does.
          t.setPendingPhase(phase);
        } else if (phase === 'tool_intro' || phase === 'tool_intro_post') {
          // Mirror Tool.tsx: on mobile the platform-sidebar / nav-dashboard
          // anchors live in the mobile menu, so open it before startTour runs
          // (and before TourProvider.resolveSteps drops missing anchors).
          t.setSteps(stepsForPhase(phase));
          startTourWithMobileMenu(() => t.startTour(phase));
        } else {
          t.setSteps(stepsForPhase(phase));
          t.startTour(phase);
        }
      }, 80);
    },

    populateGenerated: () => {
      const posts = usePostsStore.getState();
      posts.setCurrentWorkspace(WORKSPACE_ID);
      posts.setSelectedPlatforms(['linkedin', 'instagram']);
      posts.setGeneratedData({
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        linkedin: buildPlatformData('linkedin') as any,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        instagram: buildPlatformData('instagram') as any,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } as any);
      posts.setSharedPrinciples(['Social Proof', 'Authority']);
      posts.setSharedExplanation(MOCK_PSYCH_SELECTION.explanation);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      posts.setPsychologySelection(MOCK_PSYCH_SELECTION as any);
      posts.setIsGenerating(false);
    },

    /**
     * A REAL generation, not just its result: flips isGenerating true → false
     * with data landing in between. `populateGenerated` only ever set the result,
     * so it never produced the true→false edge the post-gen tour trigger listens
     * for — which is precisely why a trigger that could never fire survived so
     * long. Use this to exercise the post-generation chapter end to end.
     */
    simulateGeneration: async () => {
      const posts = usePostsStore.getState();
      posts.setCurrentWorkspace(WORKSPACE_ID);
      posts.setSelectedPlatforms(['linkedin', 'instagram']);
      posts.setIsGenerating(true);
      await new Promise((r) => setTimeout(r, 600));
      posts.setGeneratedData({
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        linkedin: buildPlatformData('linkedin') as any,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        instagram: buildPlatformData('instagram') as any,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } as any);
      posts.setSharedPrinciples(['Social Proof', 'Authority']);
      posts.setSharedExplanation(MOCK_PSYCH_SELECTION.explanation);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      posts.setPsychologySelection(MOCK_PSYCH_SELECTION as any);
      posts.setIsGenerating(false);
    },

    /**
     * End the running phase without persisting completion — exactly what a
     * chapter's final step does via `endTour(false)`. Lets a test reproduce the
     * hand-off between chapters (e.g. the generate gate ending `tool_intro`)
     * without having to satisfy every hands-on gate through the real UI.
     */
    endPhase: () => {
      void useTourStore.getState().endTour(false);
    },

    clearGenerated: () => {
      usePostsStore.getState().clearGenerated();
    },

    closeModals: () => {
      const tour = useTourStore.getState();
      tour.closeWelcomeModal();
      tour.closeCheckpointModal();
      tour.closeCloseConfirmModal();
      tour.closeEndScreen();
    },

    resetTutorial: () => {
      const fresh = { ...MOCK_PREFERENCES, has_completed_tutorial: false, tutorial_dismissed_at: null };
      try {
        localStorage.setItem('dev_preferences', JSON.stringify(fresh));
      } catch {
        // ignore
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      usePreferencesStore.setState({ preferences: fresh as any });
      const tour = useTourStore.getState();
      void tour.endTour(false);
      useTourStore.setState({ lastCompletedPhase: null, pendingPhase: null });
    },

    getState: () => {
      const tour = useTourStore.getState();
      const ws = useWorkspaceStore.getState();
      const posts = usePostsStore.getState();
      return {
        tour: {
          isActive: tour.isActive,
          currentPhase: tour.currentPhase,
          currentStep: tour.currentStep,
          stepCount: tour.currentSteps.length,
          pendingPhase: tour.pendingPhase,
          lastCompletedPhase: tour.lastCompletedPhase,
          showWelcomeModal: tour.showWelcomeModal,
          showCheckpointModal: tour.showCheckpointModal,
          showEndScreen: tour.showEndScreen,
        },
        prefs: usePreferencesStore.getState().preferences,
        ws: ws.activeWorkspace
          ? {
              id: ws.activeWorkspace.id,
              is_onboarded: ws.activeWorkspace.is_onboarded,
              services: ws.activeWorkspace.services?.length ?? 0,
            }
          : null,
        personas: ws.personas.length,
        posts: {
          selectedPlatforms: posts.selectedPlatforms,
          generatedKeys: Object.keys(posts.generatedData),
        },
      };
    },
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).__zupaTourDev = api;
}
